/**
 * Cubic bezier easing function.
 *
 * Based on the well-known De Casteljau algorithm for cubic bezier curves.
 * Originally from React Native (MIT license, Facebook Inc).
 * Re-implemented with clean attribution.
 *
 * MIT License — Copyright (c) Meta Platforms, Inc. and affiliates.
 */

const NEWTON_ITERATIONS = 4;
const NEWTON_MIN_SLOPE = 0.001;
const SUBDIVISION_PRECISION = 0.0000001;
const SUBDIVISION_MAX_ITERATIONS = 10;

const TABLE_SIZE = 11;
const STEP_SIZE = 1.0 / (TABLE_SIZE - 1.0);

function a(a1, a2) { return 1.0 - 3.0 * a2 + 3.0 * a1; }
function b(a1, a2) { return 3.0 * a2 - 6.0 * a1; }
function c(a1) { return 3.0 * a1; }

function calcBezier(t, a1, a2) {
  return ((a(a1, a2) * t + b(a1, a2)) * t + c(a1)) * t;
}

function getSlope(t, a1, a2) {
  return 3.0 * a(a1, a2) * t * t + 2.0 * b(a1, a2) * t + c(a1);
}

function binarySubdivide(x, lo, hi, x1, x2) {
  let mid, currentX, i = 0;
  do {
    mid = lo + (hi - lo) / 2.0;
    currentX = calcBezier(mid, x1, x2) - x;
    if (currentX > 0.0) hi = mid;
    else lo = mid;
  } while (Math.abs(currentX) > SUBDIVISION_PRECISION && ++i < SUBDIVISION_MAX_ITERATIONS);
  return mid;
}

function newtonRaphson(x, guessT, x1, x2) {
  for (let i = 0; i < NEWTON_ITERATIONS; ++i) {
    const slope = getSlope(guessT, x1, x2);
    if (slope === 0.0) return guessT;
    guessT -= (calcBezier(guessT, x1, x2) - x) / slope;
  }
  return guessT;
}

/**
 * Creates a cubic bezier easing function.
 * @param {number[]} points - [x1, y1, x2, y2] control points (x values in [0,1])
 * @returns {function} Easing function mapping [0,1] → [0,1]
 */
export function bezier([x1, y1, x2, y2]) {
  if (!(x1 >= 0 && x1 <= 1 && x2 >= 0 && x2 <= 1)) {
    throw new Error('bezier x values must be in [0, 1] range');
  }

  // Precompute sample table
  const samples = new Float32Array(TABLE_SIZE);
  if (x1 !== y1 || x2 !== y2) {
    for (let i = 0; i < TABLE_SIZE; ++i) {
      samples[i] = calcBezier(i * STEP_SIZE, x1, x2);
    }
  }

  function getTForX(x) {
    let intervalStart = 0.0;
    let current = 1;
    const last = TABLE_SIZE - 1;

    for (; current !== last && samples[current] <= x; ++current) {
      intervalStart += STEP_SIZE;
    }
    --current;

    const dist = (x - samples[current]) / (samples[current + 1] - samples[current]);
    const guessT = intervalStart + dist * STEP_SIZE;
    const slope = getSlope(guessT, x1, x2);

    if (slope >= NEWTON_MIN_SLOPE) return newtonRaphson(x, guessT, x1, x2);
    if (slope === 0.0) return guessT;
    return binarySubdivide(x, intervalStart, intervalStart + STEP_SIZE, x1, x2);
  }

  return function (x) {
    if (x1 === y1 && x2 === y2) return x;
    if (x === 0) return 0;
    if (x === 1) return 1;
    return calcBezier(getTForX(x), y1, y2);
  };
}
